import { useEffect, useState, useCallback, useRef } from "react";
import type { ReactElement } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import axios from "../../../api/axios";
import { useCRUD } from "../../../api/crud";
import { useApiRequest } from "../../../api/axios";
import CustomModal from "../modal/modal.component";
import CustomCircularProgress from "../customCircularProgress/customCircularProgress.component";
import EquipmentModelForm from "../equipmentModelForm/equipmentModelForm.component";
import EquipmentCounterForm from "../equipmentCounterForm/equipmentCounterForm.component";
import {
  BLACK_ONLY_EQUIPMENT_KIND,
  EQUIPMENTS_ENDPOINT,
  EQUIPMENT_ATTRIBUTES_ENDPOINT,
  EQUIPMENT_MODELS_ENDPOINT,
} from "../../../consts";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { DRFField } from "../../../types/dynamicForm";
import { OPENING_HOUR_WEEK_DAY_OPTIONS } from "../../../utils";
import { useHasPermission, useTenantName } from "../../../contexts/auth";
import styles from "../dynamicForm/dynamicForm.module.css";

const INSTALLATION_ADDRESS_FIELDS: { name: string; labelKey: string }[] = [
  { name: "city", labelKey: "City" },
  { name: "street", labelKey: "Street" },
  { name: "phone_number", labelKey: "Phone number" },
  { name: "zip_code", labelKey: "Zip code" },
  { name: "email", labelKey: "Email" },
  { name: "email_counter_reminders", labelKey: "Email for counter reminders" },
  { name: "country", labelKey: "Country" },
  { name: "address_notes", labelKey: "Notes" },
  { name: "opening_hours", labelKey: "Opening hours" },
];

const USAGE_COST_FIELDS: [name: string, labelKey: string][] = [
  ["bp_usage_cost", "Black page cost"],
  ["cp_usage_cost", "Color page cost"],
  ["sp_usage_cost", "Scanned page cost"],
];

// Black-only equipment never prints color pages, so it has no color page cost -
// same rule the counter form applies to counter kinds.
const getUsageCostFields = (equipmentKind?: string) =>
  equipmentKind === BLACK_ONLY_EQUIPMENT_KIND
    ? USAGE_COST_FIELDS.filter(([name]) => name !== "cp_usage_cost")
    : USAGE_COST_FIELDS;

interface OpeningHourRow {
  week_day: string;
  from_hour: string;
  to_hour: string;
}

interface CounterDraft {
  kind?: unknown;
  provider?: unknown;
  value?: unknown;
  read_at?: unknown;
}

interface AttributeChoice {
  id: number;
  name: string;
}

interface AttributeDefinition {
  id: number;
  name: string;
  choices: AttributeChoice[];
}

interface AttributeValueInput {
  attribute: number;
  choice: number;
  note?: string;
}

export interface EquipmentFormProps {
  onSuccess?: (data?: any) => void;
  id?: string;
  /** Render only the installation address section (requires `id`) - e.g. for a "change address" action elsewhere. */
  onlyInstallationAddress?: boolean;
}

export default function EquipmentForm({
  onSuccess,
  id,
  onlyInstallationAddress,
}: EquipmentFormProps): ReactElement {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  // Where a machine is when it is not installed at a client's site: with us.
  const tenantName = useTenantName();
  const { get, create, update, options, loading, error } = useCRUD(EQUIPMENTS_ENDPOINT);
  const { executeRequest } = useApiRequest();
  const [actions, setActions] = useState<Record<string, DRFField> | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [counterFormsData, setCounterFormsData] = useState<CounterDraft[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [modelOptions, setModelOptions] = useState<{ id: number; display_name: string }[]>([]);
  const [modelLoading, setModelLoading] = useState(false);
  const [addModelModalOpen, setAddModelModalOpen] = useState(false);
  const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
  const [attributeLoading, setAttributeLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchModelOptions = useCallback(
    async (search?: string) => {
      setModelLoading(true);
      try {
        const params = new URLSearchParams();
        if (search && search.trim().length >= 2) params.set("search", search.trim());
        const { data } = await executeRequest(() =>
          axios.get(`${EQUIPMENT_MODELS_ENDPOINT}${params.toString() ? "?" + params.toString() : ""}`)
        );
        const list = Array.isArray(data?.results) ? data.results : data || [];
        setModelOptions(list);
      } catch {
        setModelOptions([]);
      } finally {
        setModelLoading(false);
      }
    },
    [executeRequest]
  );

  useEffect(() => {
    let cancelled = false;
    options().then((response: any) => {
      if (cancelled) return;
      const postActions = response?.data?.actions?.POST;
      if (postActions) setActions(postActions);
    });
    return () => {
      cancelled = true;
    };
  }, [options]);

  useEffect(() => {
    if (!actions) return;
    if (!id) {
      const initial: Record<string, unknown> = {};
      Object.entries(actions).forEach(([name, field]) => {
        if (field.initial !== undefined && field.initial !== null) {
          initial[name] = field.initial;
        }
      });
      setFormData((prev) => ({ ...initial, ...prev }));
      setFormData((prev) => ({
        ...prev,
        opening_hours: Array.isArray(prev.opening_hours) ? prev.opening_hours : [],
      }));
      setIsLoadingInitial(false);
      return;
    }
    get(id).then((response) => {
      if (!response?.data) {
        setIsLoadingInitial(false);
        return;
      }
      const d = response.data;
      const addr = d.installation_address || {};
      const flat: Record<string, unknown> = {
        kind: d.kind,
        ownership: d.ownership,
        model_id: d.model_id ?? (d.model && typeof d.model === "object" ? (d.model as { id?: number }).id : null),
        is_installed: d.is_installed,
        serial: d.serial ?? "",
        notes: d.notes ?? "",
        bp_usage_cost: d.bp_usage_cost ?? "",
        cp_usage_cost: d.cp_usage_cost ?? "",
        sp_usage_cost: d.sp_usage_cost ?? "",
        country: addr.country ?? "PL",
        city: addr.city ?? "",
        street: addr.street ?? "",
        zip_code: addr.zip_code ?? "",
        phone_number: addr.phone_number ?? "",
        email: addr.email ?? "",
        email_counter_reminders: addr.email_counter_reminders ?? "",
        address_notes: addr.notes ?? "",
        opening_hours: Array.isArray(addr.opening_hours)
          ? addr.opening_hours.map((oh: any) => ({
              week_day: oh.week_day ?? "1",
              from_hour: oh.from_hour ?? "08:00",
              to_hour: oh.to_hour ?? "17:00",
            }))
          : [],
        attribute_values: Array.isArray(d.attribute_values)
          ? d.attribute_values.map((value: any) => ({
              attribute:
                typeof value.attribute === "number"
                  ? value.attribute
                  : value.attribute_info?.id,
              choice:
                typeof value.choice === "number"
                  ? value.choice
                  : value.choice?.id ?? value.choice_info?.id,
              note: value.note ?? "",
            }))
          : [],
      };
      setFormData(flat);
      setIsLoadingInitial(false);
    }).catch(() => setIsLoadingInitial(false));
  }, [actions, id, get]);

  useEffect(() => {
    if (onlyInstallationAddress) return;
    const modelId = formData.model_id;
    if (modelId != null && modelOptions.length === 0 && !modelLoading) {
      const idToFetch = typeof modelId === "object" && modelId !== null && "id" in modelId
        ? (modelId as { id: number }).id
        : modelId;
      executeRequest(() => axios.get(`${EQUIPMENT_MODELS_ENDPOINT}${idToFetch}/`)).then((res) => {
        if (res?.data) setModelOptions([res.data]);
      });
    }
  }, [formData.model_id, modelOptions.length, modelLoading, executeRequest, onlyInstallationAddress]);

  useEffect(() => {
    if (onlyInstallationAddress) return;
    const fetchAttributeDefinitions = async () => {
      setAttributeLoading(true);
      try {
        const listResponse = await executeRequest(() =>
          axios.get(`${EQUIPMENT_ATTRIBUTES_ENDPOINT}?page_size=200`)
        );
        const list = Array.isArray(listResponse?.data?.results)
          ? listResponse.data.results
          : [];
        const mapped: AttributeDefinition[] = list
          .filter((a: any) => a && typeof a.id === "number")
        setAttributeDefinitions(mapped);
      } finally {
        setAttributeLoading(false);
      }
    };
    fetchAttributeDefinitions();
  }, [executeRequest, onlyInstallationAddress]);

  const handleAddModelSuccess = useCallback((data?: { id: number; display_name: string }) => {
    if (data) {
      setModelOptions((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      setFormData((prev) => ({ ...prev, model_id: data.id }));
    }
    setAddModelModalOpen(false);
  }, []);

  const setField = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const ADDRESS_FORM_FIELDS = [
    "country",
    "city",
    "street",
    "zip_code",
    "phone_number",
    "email",
    "email_counter_reminders",
    "address_notes",
  ] as const;

  // Country alone doesn't count as an address - handleSubmit drops it from the
  // payload in that case, and the form defaults it to PL even for a machine
  // that has never had an address, so it must not light the button up.
  const hasAnyAddressData =
    ADDRESS_FORM_FIELDS.some(
      (name) => name !== "country" && String(formData[name] ?? "").trim() !== ""
    ) ||
    ((formData.opening_hours as OpeningHourRow[]) || []).length > 0 ||
    Boolean(formData.is_installed);

  /** Empties the address section and clears `is_installed` - the two go
   *  together, since the API refuses equipment flagged as installed with no
   *  address. Only edits the form; the change lands on save like any other. */
  const markAsReturnedToTenant = () => {
    setFormData((prev) => ({
      ...prev,
      ...Object.fromEntries(ADDRESS_FORM_FIELDS.map((name) => [name, ""])),
      opening_hours: [],
      is_installed: false,
    }));
  };

  const openingHours = (formData.opening_hours as OpeningHourRow[]) || [];
  const attributeValues = (formData.attribute_values as AttributeValueInput[]) || [];

  const getSelectedAttributeValue = (attribute: AttributeDefinition) =>
    attributeValues.find((value) => Number(value.attribute) === attribute.id);

  const updateAttributeChoice = (
    attribute: AttributeDefinition,
    choiceId: number | null
  ) => {
    setFormData((prev) => {
      const prevValues = (prev.attribute_values as AttributeValueInput[]) || [];
      const withoutCurrent = prevValues.filter(
        (value) => Number(value.attribute) !== attribute.id
      );

      if (choiceId == null) {
        return { ...prev, attribute_values: withoutCurrent };
      }

      const existing = prevValues.find(
        (value) => Number(value.attribute) === attribute.id
      );

      return {
        ...prev,
        attribute_values: [
          ...withoutCurrent,
          {
            attribute: attribute.id,
            choice: choiceId,
            note: existing?.note ?? "",
          },
        ],
      };
    });
  };

  const updateAttributeNote = (attribute: AttributeDefinition, note: string) => {
    setFormData((prev) => {
      const prevValues = (prev.attribute_values as AttributeValueInput[]) || [];
      const current = prevValues.find(
        (value) => Number(value.attribute) === attribute.id
      );
      if (!current) {
        return prev;
      }
      return {
        ...prev,
        attribute_values: prevValues.map((value) =>
          value === current ? { ...value, note } : value
        ),
      };
    });
  };

  const addOpeningHour = () => {
    setFormData((prev) => ({
      ...prev,
      opening_hours: [
        ...(prev.opening_hours as OpeningHourRow[]),
        { week_day: "1", from_hour: "08:00", to_hour: "17:00" },
      ],
    }));
  };
  const removeOpeningHour = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      opening_hours: (prev.opening_hours as OpeningHourRow[]).filter((_, i) => i !== index),
    }));
  };
  const updateOpeningHour = (index: number, field: keyof OpeningHourRow, value: string) => {
    setFormData((prev) => {
      const next = [...(prev.opening_hours as OpeningHourRow[])];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, opening_hours: next };
    });
  };

  const addCounterForm = () => {
    setCounterFormsData((prev) => [...prev, {}]);
  };

  const removeCounterForm = (index: number) => {
    setCounterFormsData((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCounterForm = (index: number, data: Record<string, unknown>) => {
    setCounterFormsData((prev) => {
      if (prev[index] === data) {
        return prev;
      }
      const next = [...prev];
      next[index] = data;
      return next;
    });
  };

  const handleSubmit = async () => {
    const countersToAttach = !id
      ? counterFormsData
          .filter((counter) => counter.value != null && counter.value !== "" && Boolean(counter.kind))
          .map((counter) => ({
            kind: counter.kind,
            provider: counter.provider ?? "C",
            value: counter.value,
            read_at: counter.read_at,
          }))
      : [];

    const attributeValuesPayload = attributeValues
      .filter((value) => value.attribute != null && value.choice != null)
      .map((value) => ({
        attribute: Number(value.attribute),
        choice: Number(value.choice),
        note: (value.note ?? "").trim(),
      }));

    // Costs that don't apply to the selected kind are sent as null rather than
    // left as-is, so switching Color -> Black clears the now-hidden (and
    // meaningless) color page cost instead of silently keeping it.
    const applicableUsageCostFields = new Set(
      getUsageCostFields(formData.kind as string | undefined).map(([name]) => name)
    );
    const usageCostPayload = Object.fromEntries(
      USAGE_COST_FIELDS.map(([name]) => {
        const value = formData[name];
        const isBlank = value === "" || value == null;
        return [name, !applicableUsageCostFields.has(name) || isBlank ? null : value];
      })
    );

    const payload: Record<string, unknown> = {
      kind: formData.kind,
      ownership: formData.ownership,
      model_id: formData.model_id,
      is_installed: formData.is_installed,
      serial: formData.serial,
      notes: formData.notes,
      ...usageCostPayload,
      country: formData.country,
      city: formData.city,
      street: formData.street,
      zip_code: formData.zip_code,
      phone_number: formData.phone_number,
      email: formData.email,
      email_counter_reminders: formData.email_counter_reminders,
      address_notes: formData.address_notes,
      opening_hours: openingHours.map((oh: OpeningHourRow) => ({
        week_day: oh.week_day,
        from_hour: oh.from_hour,
        to_hour: oh.to_hour,
      })),
      attribute_values: attributeValuesPayload,
      ...(countersToAttach.length > 0
        ? {
            counters: countersToAttach,
          }
        : {}),
    };
    const addressFields = ["country", "city", "street", "zip_code", "phone_number", "email", "email_counter_reminders", "address_notes"] as const;
    const isAddressFieldFilled = (key: string) => {
      const v = payload[key];
      if (key === "opening_hours") return Array.isArray(payload.opening_hours) && payload.opening_hours.length > 0;
      return v != null && v !== "";
    };
    const filledAddressCount = addressFields.filter((k) => isAddressFieldFilled(k)).length
      + (isAddressFieldFilled("opening_hours") ? 1 : 0);
    if (filledAddressCount === 1 && isAddressFieldFilled("country")) {
      delete payload.country;
    }
    const cleaned = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined)
    ) as Record<string, unknown>;
    if (id) {
      const result = await update({ id, ...cleaned });
      if (result?.success) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
      } else {
        const msg =
          (typeof result?.errorData === "string" ? result.errorData : result?.errorData?.message) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    } else {
      const result = await create(cleaned);
      if (result?.data) {
        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        onSuccess?.(result.data);
      } else {
        const err = result as { errorData?: string | { message?: string } };
        const msg =
          (typeof err?.errorData === "string" ? err.errorData : err?.errorData?.message) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(msg, ERROR_SNACKBAR);
      }
    }
  };

  const getFieldError = (fieldName: string) => {
    const e = error?.[fieldName];
    return Array.isArray(e) && e.length > 0 ? (e[0] as { message?: string })?.message : null;
  };

  if (!actions || isLoadingInitial) {
    return <CustomCircularProgress />;
  }

  const kindChoices = actions.kind?.type === "choice" ? actions.kind.choices ?? [] : [];
  const ownershipChoices = actions.ownership?.type === "choice" ? actions.ownership.choices ?? [] : [];
  const countryChoices = actions.country?.type === "choice" ? actions.country.choices ?? [] : [];

  const selectedModel = modelOptions.find((m: { id: number; display_name: string }) => m.id === formData.model_id) || null;
  const addressScalarFields = INSTALLATION_ADDRESS_FIELDS.filter((f) => f.name !== "opening_hours");

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {!onlyInstallationAddress && (
        <>
        {/* kind */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" }, maxWidth: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{t(actions.kind?.label || "Kind")}</InputLabel>
            <Select
              label={t(actions.kind?.label || "Kind")}
              value={(formData.kind as string) ?? ""}
              onChange={(e) => setField("kind", e.target.value)}
            >
              {kindChoices.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {t(c.display_name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getFieldError("kind") && <div className={styles.errorMessage}>{getFieldError("kind")}</div>}
        </Box>

        {/* ownership */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" }, maxWidth: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <FormControl fullWidth size="small" required>
            <InputLabel>{t(actions.ownership?.label || "Ownership")}</InputLabel>
            <Select
              label={t(actions.ownership?.label || "Ownership")}
              value={(formData.ownership as string) ?? ""}
              onChange={(e) => setField("ownership", e.target.value)}
            >
              {ownershipChoices.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {t(c.display_name)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {getFieldError("ownership") && <div className={styles.errorMessage}>{getFieldError("ownership")}</div>}
        </Box>

        {/* model_id with Add button */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" }, maxWidth: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <Autocomplete
            size="small"
            options={modelOptions}
            value={selectedModel}
            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.display_name ?? "")}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            loading={modelLoading}
            onOpen={() => fetchModelOptions()}
            onInputChange={(_, value, reason) => {
              if (reason === "input" && value) {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => fetchModelOptions(value), 300);
              } else if (reason === "reset" || !value) {
                fetchModelOptions();
              }
            }}
            onChange={(_, newVal) => setField("model_id", newVal?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Model")}
                required
                fullWidth
              />
            )}
            noOptionsText={t("No options")}
            slotProps={{
              listbox: {
                component: (props: { children?: React.ReactNode } & Record<string, unknown>) => (
                  <ul {...props}>
                    <Box component="li" sx={{ listStyle: "none", p: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Add />}
                        fullWidth
                        onClick={() => setAddModelModalOpen(true)}
                      >
                        {t("Add new model")}
                      </Button>
                    </Box>
                    {props.children}
                  </ul>
                ),
              },
            }}
          />
          {getFieldError("model_id") && <div className={styles.errorMessage}>{getFieldError("model_id")}</div>}
        </Box>

        {/* serial */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" }, maxWidth: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <TextField
            size="small"
            label={t("Serial")}
            value={(formData.serial as string) ?? ""}
            onChange={(e) => setField("serial", e.target.value)}
            required
            fullWidth
          />
          {getFieldError("serial") && <div className={styles.errorMessage}>{getFieldError("serial")}</div>}
        </Box>

        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" }, maxWidth: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <TextField
            size="small"
            label={t("Notes")}
            value={(formData.notes as string) ?? ""}
            onChange={(e) => setField("notes", e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {getFieldError("notes") && <div className={styles.errorMessage}>{getFieldError("notes")}</div>}
        </Box>

        {/* is_installed */}
        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" }, maxWidth: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(formData.is_installed)}
                onChange={(e) => setField("is_installed", e.target.checked)}
              />
            }
            label={actions.is_installed.label}
            sx={{ color: "var(--textColor)" }}
          />
          {getFieldError("is_installed") && <div className={styles.errorMessage}>{getFieldError("is_installed")}</div>}
        </Box>
        </>
        )}

        {!id && !onlyInstallationAddress && (
          <Box sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, border: "1px solid #ccc", borderRadius: 2, p: 2 }}>
              <Typography variant="h6" sx={{ color: "var(--textColor)" }}>
                  {t("Counters")}
                </Typography>

              {counterFormsData.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #eee",
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: "var(--componentBgColor)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                      {t("Counter")} #{index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeCounterForm(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                  <EquipmentCounterForm
                    hiddenFields={["equipment_id", "job"]}
                    hideSubmit
                    onFormDataChange={(data) => updateCounterForm(index, data)}
                  />
                </Box>
              ))}

              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button startIcon={<Add />} variant="outlined" size="small" onClick={addCounterForm}>
                  {t("Add counter")}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {!onlyInstallationAddress && canViewProfitability && (
        <Box sx={{ width: "100%", border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: "var(--textColor)" }}>
            {t("Usage costs")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {getUsageCostFields(formData.kind as string | undefined).map(([name, label]) => (
              <Box key={name} sx={{ flex: { xs: "1 1 100%", sm: "1 1 30%" } }}>
                <TextField
                  size="small"
                  type="number"
                  label={t(label)}
                  value={(formData[name] as string) ?? ""}
                  onChange={(e) => setField(name, e.target.value)}
                  fullWidth
                  inputProps={{ step: "0.0001", min: "0" }}
                />
                {getFieldError(name) && <div className={styles.errorMessage}>{getFieldError(name)}</div>}
              </Box>
            ))}
          </Box>
        </Box>
        )}

        {!onlyInstallationAddress && (
        <Box sx={{ width: "100%", border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 1 }}>
          <Typography variant="h6" sx={{ mb: 2, color: "var(--textColor)" }}>
            {t("Configuration")}
          </Typography>
          {attributeLoading ? (
            <Typography variant="body2" color="text.secondary">
              {t("Loading...")}
            </Typography>
          ) : attributeDefinitions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("No configurations available")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {attributeDefinitions.map((attribute) => {
                const selected = getSelectedAttributeValue(attribute);
                const choiceOptions = attribute.choices.map((c) => ({
                  id: c.id,
                  name: c.name,
                }));
                const selectedChoice =
                  selected != null
                    ? choiceOptions.find((c) => c.id === selected.choice) ?? null
                    : null;
                return (
                  <Box key={attribute.id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                      {attribute.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Autocomplete
                        options={choiceOptions}
                        value={selectedChoice}
                        onChange={(_, v, reason) => {
                          if (reason === "clear" || v == null) {
                            updateAttributeChoice(attribute, null);
                            return;
                          }
                          updateAttributeChoice(attribute, v.id);
                        }}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(o) => o.name}
                        clearIcon={<Close fontSize="small" />}
                        renderInput={(params) => (
                          <TextField {...params} size="small" label={t("Choice")} fullWidth />
                        )}
                      />
                      <TextField
                        size="small"
                        label={t("Note")}
                        value={selected?.note ?? ""}
                        onChange={(e) => updateAttributeNote(attribute, e.target.value)}
                        disabled={!selected}
                        fullWidth
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
        )}

        {/* Installation address group */}
        <Box sx={{ width: "100%", border: "1px solid #ccc", borderRadius: 2, p: 2, mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: "var(--textColor)" }}>
              {t("Installation address")}
            </Typography>
            {tenantName && (
              <Button
                size="small"
                variant="outlined"
                onClick={markAsReturnedToTenant}
                disabled={!hasAnyAddressData}
              >
                {t("Mark as returned to {{tenant}}", { tenant: tenantName })}
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {addressScalarFields.map((field) => {
              if (field.name === "country") {
                return (
                  <Box key={field.name} sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t(field.labelKey)}</InputLabel>
                      <Select
                        label={t(field.labelKey)}
                        value={(formData.country as string) ?? "PL"}
                        onChange={(e) => setField("country", e.target.value)}
                      >
                        {/* An empty country is what an address-less machine
                            has - without a matching option the Select would
                            have no value to show after "Mark as returned". */}
                        <MenuItem value="">
                          <em>—</em>
                        </MenuItem>
                        {(countryChoices.length ? countryChoices : [{ value: "PL", display_name: "Poland" }]).map((c) => (
                          <MenuItem key={c.value} value={c.value}>
                            {t(c.display_name)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {getFieldError(field.name) && <div className={styles.errorMessage}>{getFieldError(field.name)}</div>}
                  </Box>
                );
              }
              const multiline = field.name === "address_notes";
              return (
                <Box key={field.name} sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%" } }}>
                  <TextField
                    size="small"
                    label={t(field.labelKey)}
                    value={String(formData[field.name] ?? "")}
                    onChange={(e) => setField(field.name, e.target.value)}
                    fullWidth
                    multiline={multiline}
                    minRows={multiline ? 2 : undefined}
                  />
                  {getFieldError(field.name) && <div className={styles.errorMessage}>{getFieldError(field.name)}</div>}
                </Box>
              );
            })}
          </Box>

          {/* Opening hours (same structure as ClientForm) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ p: 2, border: "1px solid #ccc", borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: "var(--textColor)" }}>
                  {t("Opening hours")}
                </Typography>
                {openingHours.map((oh, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 1,
                      p: 2,
                      mb: 1,
                      backgroundColor: "var(--componentBgColor)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ color: "var(--textColor)" }}>
                        {t("Opening hours")} #{index + 1}
                      </Typography>
                      <IconButton size="small" color="error" onClick={() => removeOpeningHour(index)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          width: "100%",
                          flexWrap: { xs: "wrap", sm: "nowrap" },
                        }}
                      >
                        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ minWidth: 160, "& .MuiInputBase-root": { color: "var(--textColor)" } }}
                          >
                            <InputLabel>{t("Week day")}</InputLabel>
                            <Select
                              label={t("Week day")}
                              value={oh.week_day}
                              onChange={(e) => updateOpeningHour(index, "week_day", e.target.value)}
                            >
                              {OPENING_HOUR_WEEK_DAY_OPTIONS.map((wd) => (
                                <MenuItem key={wd.value} value={wd.value}>
                                  {t(wd.labelKey)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                          <TimeField
                            size="small"
                            label={t("From")}
                            value={oh.from_hour ? dayjs(oh.from_hour, "HH:mm") : null}
                            onChange={(value: Dayjs | null) =>
                              updateOpeningHour(index, "from_hour", value ? value.format("HH:mm") : "08:00")
                            }
                            format="HH:mm"
                            fullWidth
                            sx={{ "& .MuiInputBase-root": { color: "var(--textColor)" } }}
                          />
                        </Box>
                        <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 0" }, minWidth: 0 }}>
                          <TimeField
                            size="small"
                            label={t("To")}
                            value={oh.to_hour ? dayjs(oh.to_hour, "HH:mm") : null}
                            onChange={(value: Dayjs | null) =>
                              updateOpeningHour(index, "to_hour", value ? value.format("HH:mm") : "17:00")
                            }
                            format="HH:mm"
                            fullWidth
                            sx={{ "& .MuiInputBase-root": { color: "var(--textColor)" } }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
                <Button startIcon={<Add />} variant="outlined" size="small" onClick={addOpeningHour}>
                  {t("Add")} {t("Opening hours")}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
          <Button variant="contained" color="success" disabled={loading} onClick={handleSubmit}>
            {t("Submit")}
          </Button>
        </Box>
      </Box>

      <CustomModal
        opened={addModelModalOpen}
        setOpened={setAddModelModalOpen}
        title={t("Add new model")}
        content={<EquipmentModelForm onSuccess={handleAddModelSuccess} />}
      />
    </>
  );
}
